import { getEditorOpenAiApiKey } from "site/services/editor-tts.service";
import { readEditorProofreadPromptOverride } from "site/services/editor-proofread-prompt.service";
import {
  buildProofreadLessonZeptoPayload,
  collectAllProofreadItems,
  collectProofreadItemsForRow,
  type ProofreadItem,
  type ProofreadLessonZeptoPayload,
  type ZeptoRowEdit,
  zeptoToRows,
} from "site/services/zepto-editor";

const OPENAI_CHAT_URL = "https://api.openai.com/v1/chat/completions";
const PROOFREAD_MODEL = "gpt-5.4-nano";

export function proofreadTextUnchanged(current: string, suggested: string): boolean {
  if (current === suggested) {
    return true;
  }
  const suggestedTrim = suggested.trim();
  if (!suggestedTrim) {
    return true;
  }
  const currentTrim = current.trim();
  if (currentTrim === suggestedTrim) {
    return true;
  }
  const norm = (value: string) => value.normalize("NFC").replace(/\r\n/g, "\n");
  return norm(currentTrim) === norm(suggestedTrim);
}

export type ProofreadSuggestionResult = {
  id: string;
  suggested: string;
  note?: string;
};

export type ProofreadProgress = {
  batchIndex: number;
  batchCount: number;
  itemsDone: number;
  itemsTotal: number;
  percent: number;
};

export type ProofreadLessonSource = {
  rows: ZeptoRowEdit[];
  titleArabic: string;
  titleRussian: string;
};

export const DEFAULT_PROOFREAD_SYSTEM_PROMPT = [
  "Proofread an Arabic lesson (classical MSA).",
  "Input: lesson JSON — title_arabic, title_russian, zepto (array of zepto rows: [type, content, id]).",
  "Output: the same JSON — title_arabic, title_russian, zepto. Change only fields with real errors; keep everything else byte-identical. Do not change structure (row count, types, row ids).",
  "Errors only: typos, wrong/missing tashkeel, missing required translation, Arabic/Russian meaning mismatch in a row. Never invent errors or rewrite for style; if unsure — leave unchanged.",
  "Arabic: vocalize every pronounced word; keep markup # |+…| |*…*|. Russian: plain text, no | + - # * markup. Arabic and Russian in a row must match in meaning.",
  "Translation fields (title_russian and zepto content[1], including type 3): Russian only. If a translation slot contains Arabic script, that is an error — replace it with a correct Russian translation of the row's Arabic source; never leave Arabic in translation fields.",
].join(" ");

function systemPromptAll(): string {
  const custom = readEditorProofreadPromptOverride();
  if (custom) {
    return custom;
  }
  return DEFAULT_PROOFREAD_SYSTEM_PROMPT;
}

function collectProofreadItemsForSource(
  source: ProofreadLessonSource,
  rowIndex?: number,
): ProofreadItem[] {
  const { rows, titleArabic, titleRussian } = source;
  if (rowIndex != null) {
    return collectProofreadItemsForRow(rowIndex, rows, titleArabic, titleRussian);
  }
  return collectAllProofreadItems(rows, titleArabic, titleRussian);
}

function parseProofreadLessonResponse(content: string): ProofreadLessonZeptoPayload {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    throw new Error("OpenAI proofread: invalid JSON response");
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("OpenAI proofread: invalid JSON response");
  }
  const row = parsed as Record<string, unknown>;
  if (!Array.isArray(row.zepto)) {
    throw new Error("OpenAI proofread: в ответе нет zepto");
  }
  return {
    title_arabic: String(row.title_arabic ?? ""),
    title_russian: String(row.title_russian ?? ""),
    zepto: row.zepto,
  };
}

function assertProofreadStructureUnchanged(
  sourceRows: ZeptoRowEdit[],
  correctedRows: ZeptoRowEdit[],
): void {
  if (correctedRows.length !== sourceRows.length) {
    throw new Error("OpenAI proofread: изменилось число строк zepto");
  }
  for (let i = 0; i < sourceRows.length; i += 1) {
    if (sourceRows[i].rowId !== correctedRows[i].rowId) {
      throw new Error("OpenAI proofread: изменились id строк zepto");
    }
    if (sourceRows[i].type !== correctedRows[i].type) {
      throw new Error("OpenAI proofread: изменились типы строк zepto");
    }
  }
}

export function diffProofreadItems(
  originalItems: ProofreadItem[],
  correctedRows: ZeptoRowEdit[],
  correctedTitleArabic: string,
  correctedTitleRussian: string,
): ProofreadSuggestionResult[] {
  const correctedById = new Map(
    collectAllProofreadItems(correctedRows, correctedTitleArabic, correctedTitleRussian).map(
      (item) => [item.id, item.text],
    ),
  );
  const out: ProofreadSuggestionResult[] = [];
  for (const item of originalItems) {
    const suggested = correctedById.get(item.id);
    if (suggested === undefined || proofreadTextUnchanged(item.text, suggested)) {
      continue;
    }
    out.push({ id: item.id, suggested });
  }
  return out;
}

async function proofreadLessonZepto(
  lesson: ProofreadLessonZeptoPayload,
  source: ProofreadLessonSource,
  originalItems: ProofreadItem[],
  apiKey: string,
): Promise<ProofreadSuggestionResult[]> {
  const payload = {
    model: PROOFREAD_MODEL,
    response_format: { type: "json_object" },
    reasoning_effort: "medium",
    messages: [
      { role: "system", content: systemPromptAll() },
      { role: "user", content: JSON.stringify(lesson) },
    ],
  };

  const r = await fetch(OPENAI_CHAT_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`OpenAI proofread: HTTP ${r.status}${detail ? ` — ${detail}` : ""}`);
  }

  const json = (await r.json()) as {
    choices?: Array<{ message?: { content?: string } }>;
  };
  const content = json.choices?.[0]?.message?.content?.trim() ?? "";
  if (!content) {
    return [];
  }

  const corrected = parseProofreadLessonResponse(content);
  let correctedRows: ZeptoRowEdit[];
  try {
    correctedRows = zeptoToRows(corrected.zepto);
  } catch {
    throw new Error("OpenAI proofread: не удалось разобрать zepto в ответе");
  }
  assertProofreadStructureUnchanged(source.rows, correctedRows);

  return diffProofreadItems(
    originalItems,
    correctedRows,
    corrected.title_arabic,
    corrected.title_russian,
  );
}

export async function proofreadLessonAllItems(
  source: ProofreadLessonSource,
  options?: {
    rowIndex?: number;
    onProgress?: (progress: ProofreadProgress) => void;
  },
): Promise<ProofreadSuggestionResult[]> {
  const items = collectProofreadItemsForSource(source, options?.rowIndex);
  if (!items.length) {
    return [];
  }

  const lesson = buildProofreadLessonZeptoPayload(
    source.rows,
    source.titleArabic,
    source.titleRussian,
  );
  const itemsTotal = items.length;
  const onProgress = options?.onProgress;

  onProgress?.({
    batchIndex: 0,
    batchCount: 1,
    itemsDone: 0,
    itemsTotal,
    percent: 0,
  });

  const apiKey = await getEditorOpenAiApiKey();
  const results = await proofreadLessonZepto(lesson, source, items, apiKey);

  onProgress?.({
    batchIndex: 1,
    batchCount: 1,
    itemsDone: itemsTotal,
    itemsTotal,
    percent: 100,
  });

  if (options?.rowIndex == null) {
    return results;
  }
  const allowed = new Set(items.map((item) => item.id));
  return results.filter((row) => allowed.has(row.id));
}
